import React, { useCallback, useMemo } from "react";
import type { BurndownChartProps } from "../../types/burndown.types";
import {
  dateToTimelineProgress,
  timelineProgressToDate,
} from "../../utils/chart/timeline-date.utils";

interface MobileBurndownChartProps {
  data: BurndownChartProps["data"];
  currentDate: Date;
  visibleDateRange: { startDate: Date; endDate: Date };
  rawEvents: Array<{ id?: string; date?: Date | string }>;
  onDateChange?: (date: Date) => void;
}

const SLIDER_STEPS = 1000;

export const MobileBurndownChart: React.FC<MobileBurndownChartProps> = ({
  currentDate,
  visibleDateRange,
  rawEvents = [],
  onDateChange,
}) => {
  const progress = useMemo(
    () => dateToTimelineProgress(currentDate, visibleDateRange),
    [currentDate, visibleDateRange],
  );
  const progressPercentage = progress * 100;
  const sliderValue = Math.round(progress * SLIDER_STEPS);

  const emitDateFromProgress = useCallback(
    (nextProgress: number) => {
      if (!onDateChange) {
        return;
      }
      onDateChange(timelineProgressToDate(nextProgress, visibleDateRange));
    },
    [onDateChange, visibleDateRange],
  );

  const handleSliderChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    emitDateFromProgress(Number(event.target.value) / SLIDER_STEPS);
  };

  return (
    <div className="border-b border-white/10 bg-surface lg:hidden">
      <div className="px-4 py-4">
        <p className="sh-kicker mb-1">Scrub</p>
        <h3 className="mb-4 text-lg font-bold text-white">Timeline</h3>

        <div className="relative mb-4 touch-pan-y">
          <div
            className="pointer-events-none relative h-3 overflow-hidden rounded-full bg-white/10 shadow-inner"
            aria-hidden
          >
            <div
              className="absolute left-0 top-0 h-full bg-gradient-to-r from-brand to-brand-muted transition-[width] duration-150"
              style={{ width: `${progressPercentage}%` }}
            />
          </div>

          <div
            className="pointer-events-none absolute top-0 h-3 w-1 transition-[left] duration-150"
            style={{ left: `${progressPercentage}%`, transform: "translateX(-50%)" }}
            aria-hidden
          >
            <div className="h-3 w-1 rounded-full bg-brand shadow-glow" />
            <div className="absolute -top-3 left-1/2 h-4 w-4 -translate-x-1/2 rounded-full border-2 border-page bg-brand shadow-glow" />
          </div>

          {onDateChange ? (
            <input
              type="range"
              min={0}
              max={SLIDER_STEPS}
              step={1}
              value={sliderValue}
              onChange={handleSliderChange}
              className="mobile-timeline-slider absolute inset-x-0 top-1/2 z-10 h-10 w-full -translate-y-1/2 cursor-pointer appearance-none bg-transparent"
              aria-label="Scrub timeline"
              aria-valuemin={0}
              aria-valuemax={SLIDER_STEPS}
              aria-valuenow={sliderValue}
              aria-valuetext={currentDate.toLocaleDateString()}
            />
          ) : null}
        </div>

        <div className="mb-4 flex justify-between text-sm text-white/65">
          <div className="flex flex-col items-start">
            <span className="text-xs text-white/45">Start</span>
            <span className="font-medium">
              {visibleDateRange.startDate.toLocaleDateString()}
            </span>
          </div>
          <div className="flex flex-col items-center">
            <span className="text-xs font-semibold text-brand">Current</span>
            <span className="font-semibold text-brand">
              {currentDate.toLocaleDateString()}
            </span>
          </div>
          <div className="flex flex-col items-end">
            <span className="text-xs text-white/45">End</span>
            <span className="font-medium">
              {visibleDateRange.endDate.toLocaleDateString()}
            </span>
          </div>
        </div>

        {onDateChange ? (
          <p className="mb-4 text-center text-xs text-white/45">
            Drag the slider to move through time
          </p>
        ) : null}

        {rawEvents.length > 0 ? (
          <div className="rounded-lg bg-brand/10 px-4 py-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="h-2 w-2 rounded-full bg-brand" />
                <span className="text-sm font-medium text-emerald-50">
                  {rawEvents.length} event{rawEvents.length !== 1 ? "s" : ""} on timeline
                </span>
              </div>
              <span className="text-xs font-medium text-brand">Track milestones</span>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};
